import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTeam } from '../context/TeamContext';
import { updateTeam, deleteTeam, checkSlugAvailable } from '../lib/supabase';
import { OUTPUT_TYPES } from '../constants';

export default function TeamSettingsPage() {
  const { user } = useAuth();
  const { activeTeam, isTeamAdmin, isTeamOwner, refreshActiveTeam, switchTeam, loadTeams, setTeams, loadingTeams } = useTeam();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: '', slug: '',
    approval_required: false,
    members_can_publish: true,
    members_can_create_folders: true,
    members_can_add_to_folders: true,
    members_can_edit_entries: false,
    require_tags: false,
    allowed_types: ['image','video','text','longform','code','prompt'],
  });
  const [saving, setSaving]     = useState(false);
  const [slugOk, setSlugOk]     = useState(true);
  const [slugChecking, setSlugChecking] = useState(false);
  const [saved, setSaved]       = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');

  useEffect(() => {
    if (loadingTeams) return; // wait for context before redirecting
    if (loadingTeams) return; // wait for teams to load before redirecting
    if (!activeTeam) return navigate('/app');
    if (!isTeamAdmin) return navigate('/team');
    const settings = activeTeam.settings || {};
    setForm({
      name: activeTeam.name || '',
      slug: activeTeam.slug || '',
      approval_required:          settings.approval_required          ?? false,
      members_can_publish:        settings.members_can_publish        ?? true,
      members_can_create_folders: settings.members_can_create_folders ?? true,
      members_can_add_to_folders: settings.members_can_add_to_folders ?? true,
      members_can_edit_entries:   settings.members_can_edit_entries   ?? false,
      require_tags:               settings.require_tags               ?? false,
      allowed_types:              settings.allowed_types              ?? ['image','video','text','longform','code','prompt'],
    });
  }, [activeTeam?.id, loadingTeams]);

  const setV = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Check slug availability with debounce
  useEffect(() => {
    if (!form.slug || form.slug === activeTeam?.slug) { setSlugOk(true); return; }
    const timer = setTimeout(async () => {
      setSlugChecking(true);
      const ok = await checkSlugAvailable(form.slug).catch(() => false);
      setSlugOk(ok);
      setSlugChecking(false);
    }, 500);
    return () => clearTimeout(timer);
  }, [form.slug]);

  const handleSave = async () => {
    if (!slugOk) return;
    setSaving(true);
    try {
      await updateTeam(activeTeam.id, {
        name: form.name.trim(),
        slug: form.slug.trim().toLowerCase(),
        settings: {
          approval_required:          form.approval_required,
          members_can_publish:        form.members_can_publish,
          members_can_create_folders: form.members_can_create_folders,
          members_can_add_to_folders: form.members_can_add_to_folders,
          members_can_edit_entries:   form.members_can_edit_entries,
          require_tags:               form.require_tags,
          allowed_types:              form.allowed_types,
        },
      });
      await refreshActiveTeam();
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) { alert(e.message); }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (deleteConfirm.trim().toLowerCase() !== activeTeam.name.toLowerCase()) return;
    try {
      const deletedId = activeTeam.id;
      await deleteTeam(deletedId);
      // Remove from teams list immediately so sidebar updates without refresh
      setTeams(prev => prev.filter(t => t.id !== deletedId));
      await switchTeam(null);
      navigate('/app');
    } catch (e) { alert(e.message); }
  };

  const toggleType = (typeId) => {
    setForm(f => ({
      ...f,
      allowed_types: f.allowed_types.includes(typeId)
        ? f.allowed_types.filter(t => t !== typeId)
        : [...f.allowed_types, typeId],
    }));
  };

  if (loadingTeams) return (
    <div style={{ padding: 40, textAlign: 'center', color: 'var(--dim)', fontFamily: 'var(--font)', fontSize: 13 }}>
      Loading...
    </div>
  );
  if (!activeTeam) return null;

  const PLAN_LABELS = { starter: 'Starter (3 seats)', growth: 'Growth (10 seats)', agency: 'Agency (unlimited)' };

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div>
          <div style={s.pageTitle}>Team settings</div>
          <div style={{ fontSize: 13, color: 'var(--muted)' }}>{activeTeam.name}</div>
        </div>

      </div>

      <div style={s.body}>
        {/* ── General ── */}
        <div style={s.section}>
          <div style={s.sectionTitle}>General</div>

          <div style={s.field}>
            <label style={s.label}>Team name</label>
            <input style={s.input} value={form.name} onChange={e => setV('name', e.target.value)} />
          </div>

          <div style={s.field}>
            <label style={s.label}>
              Team slug
              <span style={{ fontSize: 10, color: 'var(--dim)', marginLeft: 8, fontWeight: 400 }}>
                Used in invite links
              </span>
            </label>
            <div style={{ position: 'relative' }}>
              <input
                style={{
                  ...s.input,
                  borderColor: !slugOk ? 'rgba(226,75,74,0.6)' : form.slug !== activeTeam?.slug && slugOk ? 'rgba(29,158,117,0.5)' : undefined,
                }}
                value={form.slug}
                onChange={e => setV('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
              />
              <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 12 }}>
                {slugChecking ? '…' : !slugOk ? '✗' : form.slug !== activeTeam?.slug ? '✓' : ''}
              </span>
            </div>
            {!slugOk && <div style={{ fontSize: 11, color: 'var(--danger)' }}>This slug is already taken</div>}
          </div>

          <div style={s.field}>
            <label style={s.label}>Plan</label>
            <div style={{
              background: 'var(--surface2)', border: '0.5px solid var(--border)',
              borderRadius: 'var(--radius-sm)', padding: '10px 14px',
              fontSize: 13, color: 'var(--text)', display: 'flex', justifyContent: 'space-between',
            }}>
              <span>{PLAN_LABELS[activeTeam.plan] || activeTeam.plan}</span>
              <span style={{ fontSize: 12, color: 'var(--dim)' }}>{activeTeam.seat_limit} seat limit</span>
            </div>
          </div>
        </div>

        <div style={s.divider} />

        {/* ── Billing ── */}
        {isTeamOwner && (
          <div style={s.section}>
            <div style={s.sectionTitle}>Plan & billing</div>

            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              flexWrap: 'wrap', gap: 12,
              background: 'var(--surface2)', border: '0.5px solid var(--border)',
              borderRadius: 'var(--radius-sm)', padding: '12px 16px',
            }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', marginBottom: 2 }}>
                  {PLAN_LABELS[activeTeam.plan] || activeTeam.plan}
                  {activeTeam.billing_status === 'canceled' && (
                    <span style={{ marginLeft: 8, fontSize: 10, color: 'var(--danger)', background: 'rgba(226,75,74,0.1)', padding: '2px 7px', borderRadius: 10 }}>Canceled</span>
                  )}
                  {activeTeam.billing_status === 'past_due' && (
                    <span style={{ marginLeft: 8, fontSize: 10, color: '#ef9f27', background: 'rgba(239,159,39,0.1)', padding: '2px 7px', borderRadius: 10 }}>Payment overdue</span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: 'var(--dim)' }}>
                  {activeTeam.seat_limit === 999 ? 'Unlimited seats' : `${activeTeam.seat_limit} seat limit`}
                  {activeTeam.stripe_subscription_id && activeTeam.stripe_subscription_id !== 'manual'
                    ? ' · Managed via Stripe'
                    : ' · Manual / trial'}
                </div>
              </div>
              {activeTeam.stripe_customer_id && activeTeam.stripe_customer_id !== 'manual' && (
                <button
                  onClick={async () => {
                    try {
                      const res = await fetch('/api/billing', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ customerId: activeTeam.stripe_customer_id, returnUrl: '/team/settings' }),
                      });
                      const data = await res.json();
                      if (data.url) window.location.href = data.url;
                    } catch (e) { alert(e.message); }
                  }}
                  style={{
                    background: 'var(--accent)', color: '#fff', border: 'none',
                    borderRadius: 'var(--radius-sm)', padding: '8px 16px',
                    fontFamily: 'var(--font)', fontSize: 13, fontWeight: 500, cursor: 'pointer',
                  }}>
                  Manage billing →
                </button>
              )}
              {(!activeTeam.stripe_customer_id || activeTeam.stripe_customer_id === 'manual') && (
                <a href="/team/create" style={{
                  background: 'var(--accent)', color: '#fff', borderRadius: 'var(--radius-sm)',
                  padding: '8px 16px', fontFamily: 'var(--font)', fontSize: 13, fontWeight: 500,
                  textDecoration: 'none',
                }}>
                  Add billing →
                </a>
              )}
            </div>
          </div>
        )}

        <div style={s.divider} />

        {/* ── Content settings ── */}
        <div style={s.section}>
          <div style={s.sectionTitle}>Content settings</div>

          <ToggleSetting
            label="Require approval for shared entries"
            desc="Entries shared to the team stay pending until an admin approves them"
            checked={form.approval_required}
            onChange={v => setV('approval_required', v)}
          />

          <ToggleSetting
            label="Members can publish publicly"
            desc="Allow members to mark their team prompts as public on the main feed"
            checked={form.members_can_publish}
            onChange={v => setV('members_can_publish', v)}
          />

          <ToggleSetting
            label="Members can create team folders"
            desc="Allow all members to create new folders in the team library. Disable to restrict folder creation to admins only."
            checked={form.members_can_create_folders}
            onChange={v => setV('members_can_create_folders', v)}
          />

          <ToggleSetting
            label="Members can add to folders"
            desc="Allow members to add entries into existing team folders. Disable to restrict to admins only."
            checked={form.members_can_add_to_folders}
            onChange={v => setV('members_can_add_to_folders', v)}
          />

          <ToggleSetting
            label="Members can edit others' entries"
            desc="Allow members to edit entries shared by other team members. Admins can always edit."
            checked={form.members_can_edit_entries}
            onChange={v => setV('members_can_edit_entries', v)}
          />

          <ToggleSetting
            label="Require tags on team prompts"
            desc="Members must add at least one tag when submitting to the team library"
            checked={form.require_tags}
            onChange={v => setV('require_tags', v)}
          />

          <div style={s.field}>
            <label style={s.label}>Allowed output types</label>
            <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
              {OUTPUT_TYPES.map(t => (
                <div
                  key={t.id}
                  onClick={() => toggleType(t.id)}
                  style={{
                    padding: '6px 12px', borderRadius: 20, fontSize: 12, cursor: 'pointer',
                    border: `0.5px solid ${form.allowed_types.includes(t.id) ? 'var(--accent-border)' : 'var(--border2)'}`,
                    background: form.allowed_types.includes(t.id) ? 'var(--accent-dim)' : 'var(--surface2)',
                    color: form.allowed_types.includes(t.id) ? 'var(--accent)' : 'var(--muted)',
                    transition: 'all 0.12s',
                  }}>
                  {t.icon} {t.label}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div style={s.divider} />

        {/* Save button */}
        <button
          onClick={handleSave}
          disabled={saving || !slugOk || !form.name.trim()}
          style={{
            background: saved ? 'rgba(29,158,117,0.15)' : 'var(--accent)',
            color: saved ? '#5dcaa5' : '#fff',
            border: saved ? '0.5px solid rgba(29,158,117,0.4)' : 'none',
            borderRadius: 'var(--radius-sm)', padding: '10px 24px',
            fontFamily: 'var(--font)', fontSize: 13, fontWeight: 500,
            cursor: (!slugOk || saving) ? 'not-allowed' : 'pointer',
            opacity: (!slugOk || !form.name.trim()) ? 0.5 : 1,
            transition: 'all 0.2s',
          }}>
          {saved ? '✓ Saved' : saving ? 'Saving...' : 'Save settings'}
        </button>

        {/* ── Danger zone ── */}
        {isTeamOwner && (
          <>
            <div style={{ ...s.divider, marginTop: 16 }} />
            <div style={{ ...s.section, paddingTop: 24 }}>
              <div style={{
                fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.07em',
                color: 'var(--danger)', fontWeight: 500, marginBottom: 14, opacity: 0.7,
              }}>Danger zone</div>
              <div style={{
                border: '1px solid rgba(226,75,74,0.25)', borderRadius: 10,
                overflow: 'hidden', background: 'rgba(226,75,74,0.03)',
              }}>
                <div style={{ padding: '16px 18px', borderBottom: '0.5px solid rgba(226,75,74,0.15)' }}>
                  <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text)', marginBottom: 4 }}>
                    Delete this team
                  </div>
                  <div style={{ fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.6 }}>
                    Permanently deletes the team, all shared prompts, folders, and skills.
                    Member personal libraries are unaffected. <strong style={{ color: 'var(--text)' }}>This cannot be undone.</strong>
                  </div>
                </div>
                <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div style={s.field}>
                    <label style={s.label}>
                      Type <strong style={{ color: 'var(--danger)', letterSpacing: 0, textTransform: 'none' }}>{activeTeam.name}</strong> to confirm
                      <span style={{ color: 'var(--dim)', fontWeight: 400, marginLeft: 4 }}>(not case sensitive)</span>
                    </label>
                    <input
                      style={{
                        ...s.input,
                        borderColor: deleteConfirm === activeTeam.name
                          ? 'rgba(226,75,74,0.6)'
                          : 'var(--border2)',
                      }}
                      placeholder={`e.g. ${activeTeam.name}`}
                      value={deleteConfirm}
                      onChange={e => setDeleteConfirm(e.target.value)}
                    />
                  </div>
                  <button
                    onClick={handleDelete}
                    disabled={deleteConfirm.trim().toLowerCase() !== activeTeam.name.toLowerCase()}
                    style={{
                      background: deleteConfirm.trim().toLowerCase() === activeTeam.name.toLowerCase() ? 'var(--danger)' : 'rgba(226,75,74,0.08)',
                      color: deleteConfirm.trim().toLowerCase() === activeTeam.name.toLowerCase() ? '#fff' : 'var(--danger)',
                      border: '0.5px solid rgba(226,75,74,0.4)',
                      borderRadius: 8, padding: '10px 18px',
                      fontFamily: 'var(--font)', fontSize: 13, fontWeight: 500,
                      cursor: deleteConfirm.trim().toLowerCase() !== activeTeam.name.toLowerCase() ? 'not-allowed' : 'pointer',
                      opacity: deleteConfirm.trim().toLowerCase() !== activeTeam.name.toLowerCase() ? 0.5 : 1,
                      transition: 'all 0.15s', alignSelf: 'flex-start',
                    }}>
                    🗑 Delete {activeTeam.name} permanently
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ToggleSetting({ label, desc, checked, onChange }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, padding: '12px 0', borderBottom: '0.5px solid var(--border)' }}>
      <div>
        <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', marginBottom: 3 }}>{label}</div>
        <div style={{ fontSize: 12, color: 'var(--dim)', lineHeight: 1.5 }}>{desc}</div>
      </div>
      <div
        onClick={() => onChange(!checked)}
        style={{
          width: 40, height: 22, borderRadius: 11, flexShrink: 0,
          background: checked ? 'var(--accent)' : 'var(--surface3)',
          border: '0.5px solid var(--border2)',
          position: 'relative', cursor: 'pointer', transition: 'background 0.2s',
        }}>
        <div style={{
          position: 'absolute', top: 3, left: checked ? 21 : 3,
          width: 14, height: 14, borderRadius: '50%',
          background: '#fff', transition: 'left 0.2s',
          boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
        }} />
      </div>
    </div>
  );
}

const s = {
  page:       { background: 'var(--bg)', padding: '24px', fontFamily: 'var(--font)', height: '100%', overflowY: 'auto' },
  header:     { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24, gap: 12 },
  pageTitle:  { fontSize: 20, fontWeight: 500, color: 'var(--text)', letterSpacing: '-0.02em', marginBottom: 4 },
  backBtn:    { background: 'none', border: '0.5px solid var(--border2)', borderRadius: 'var(--radius-sm)', padding: '7px 14px', color: 'var(--muted)', fontFamily: 'var(--font)', fontSize: 13, cursor: 'pointer' },
  body:       { maxWidth: 560, display: 'flex', flexDirection: 'column', gap: 20 },
  section:    { display: 'flex', flexDirection: 'column', gap: 14 },
  sectionTitle: { fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--dim)', fontWeight: 500 },
  divider:    { height: '0.5px', background: 'var(--border)' },
  field:      { display: 'flex', flexDirection: 'column', gap: 5 },
  label:      { fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--dim)', fontWeight: 500 },
  input:      { background: 'var(--surface)', border: '0.5px solid var(--border2)', borderRadius: 'var(--radius-sm)', padding: '9px 12px', color: 'var(--text)', fontFamily: 'var(--font)', fontSize: 13, outline: 'none', width: '100%' },
};